import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { styled, useTheme } from '@mui/material/styles';
import { Checkbox, useFormInputHandler } from 'swipeclock-frontend';
import { useFetcher } from 'react-router-dom';
import { useFetcherStateForKey } from 'hooks/useNavigationState';
import { setGoToConfigureNotifications } from 'store/appSlice';
import {
  useGetLoginDetailsQuery,
  useGetNotificationSettingsQuery,
} from 'services/masterDataApi';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import ErrorIcon from '@mui/icons-material/Error';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import { useFormValidation, FORM_IDS } from 'hooks/useNotificationValidation';

export default function ConfigureNotifications() {
  const fetcher = useFetcher({ key: 'configureNotifications' });
  const { success, error } = fetcher?.data ?? {};

  const { data: loginData, isSuccess: isGetLoginSuccess } =
    useGetLoginDetailsQuery();
  const { id } = loginData ?? {};

  const { isError, isUninitialized, isSuccess } =
    useGetNotificationSettingsQuery({ loginId: id }, { skip: !id });
  const hasError = isError || isUninitialized || !!error;

  return (
    <GridBox>
      <Header />
      {isSuccess && isGetLoginSuccess && (
        <ConfigureNotificationsForm
          loginId={id}
          error={hasError}
          success={!!success}
        />
      )}
      <Footer />
    </GridBox>
  );
}

ConfigureNotifications.propTypes = {};

function ConfigureNotificationsForm({ loginId, error, success }) {
  const { data } = useGetNotificationSettingsQuery({ loginId });

  // initialData is used to create a stable defaultChecked that does not change
  // when the cache is invalidated on save, but will reflect the most
  // up-to-date data on initial load.
  const initialData = useRef(data);

  const fetcher = useFetcher({ key: 'configureNotifications' });
  const isLoading = useFetcherStateForKey('configureNotifications');

  const { errorContext, validationCallback } = useFormValidation();

  const { disabled, handleChecked, validationRef } = useFormInputHandler({
    errorContext,
    ids: Object.values(FORM_IDS),
    validationCallback,
  });

  // Check if the form has been modified from its initial state - if not, we want to disable the save button
  const isModified = useIsModifiedForm(data, validationRef);

  // This function handles putting together data objects using data expected by
  // the back end, in a format that formData can handle as allowing the form to
  // submit automatically only includes the intent due to the use of checkboxes
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.set('intent', 'update-notification-configuration');
    Object.values(FORM_IDS).forEach((id) => {
      const itemToSet = {
        emailEnabled: !!validationRef.current[id]?.checked,
        pushEnabled: data[id]?.pushEnabled,
        notificationId: data[id]?.notificationId,
        selectedScope: data[id]?.selectedScope,
      };
      formData.set(id, JSON.stringify(itemToSet));
    });
    fetcher.submit(formData, { method: 'post', action: '/' });
  };

  return (
    <fetcher.Form method="post" action="/" onSubmit={handleSubmit}>
      <SuccessToast success={success} />
      <FormInputGrid error={!!error}>
        {error && <ErrorMessage />}
        <FormHeader>
          <Blurb>
            Use the checkboxes below to update your notification preferences.
          </Blurb>
          <Stack direction="row" justifyContent="space-between">
            <SubTitle>Notification Name</SubTitle>
            <SubTitle>Email</SubTitle>
          </Stack>
          <Divider />
        </FormHeader>
        <CheckboxStack spacing={2}>
          <CustomCheckbox
            id={FORM_IDS.TIME_OFF_REQUESTS}
            label={data[FORM_IDS.TIME_OFF_REQUESTS]?.notificationName}
            validationRef={validationRef}
            onChange={handleChecked(FORM_IDS.TIME_OFF_REQUESTS)}
            defaultChecked={
              initialData.current[FORM_IDS.TIME_OFF_REQUESTS]?.emailEnabled ??
              false
            }
          />
          <CustomCheckbox
            id={FORM_IDS.GEOFENCE}
            label={data[FORM_IDS.GEOFENCE]?.notificationName}
            validationRef={validationRef}
            onChange={handleChecked(FORM_IDS.GEOFENCE)}
            defaultChecked={
              initialData.current[FORM_IDS.GEOFENCE]?.emailEnabled ?? false
            }
          />
          <CustomCheckbox
            id={FORM_IDS.SHIFT_CHANGE_REQUEST}
            label={data[FORM_IDS.SHIFT_CHANGE_REQUEST]?.notificationName}
            validationRef={validationRef}
            onChange={handleChecked(FORM_IDS.SHIFT_CHANGE_REQUEST)}
            defaultChecked={
              initialData.current[FORM_IDS.SHIFT_CHANGE_REQUEST]
                ?.emailEnabled ?? false
            }
          />
          <CustomCheckbox
            id={FORM_IDS.LATE_TO_SHIFT}
            label={data[FORM_IDS.LATE_TO_SHIFT]?.notificationName}
            validationRef={validationRef}
            onChange={handleChecked(FORM_IDS.LATE_TO_SHIFT)}
            defaultChecked={
              initialData.current[FORM_IDS.LATE_TO_SHIFT]?.emailEnabled ?? false
            }
          />
        </CheckboxStack>
        <Button
          fullWidth
          name="intent"
          type="submit"
          value={'update-notification-configuration'}
          endArrow
          disabled={!isModified || disabled || success}
          loading={isLoading}
          sx={{ mt: 1, gridArea: 'save' }}
        >
          SAVE
        </Button>
      </FormInputGrid>
    </fetcher.Form>
  );
}

ConfigureNotificationsForm.propTypes = {
  loginId: PropTypes.number,
  error: PropTypes.bool,
  success: PropTypes.bool,
};

// swipeclock-frontend automatically adds the label to the right of the
// checkbox. This component acts as a wrapper to manually place the label to
// the left of the checkbox and add a divider below each item.
function CustomCheckbox({
  id,
  label,
  validationRef,
  onChange,
  defaultChecked = false,
}) {
  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <CheckboxLabel>{label}</CheckboxLabel>
        <StyledCheckbox
          id={id}
          defaultChecked={defaultChecked}
          validationRef={validationRef}
          onChange={onChange}
        />
      </Stack>
      <Divider />
    </>
  );
}

CustomCheckbox.propTypes = {
  defaultChecked: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  onChange: PropTypes.func,
  validationRef: PropTypes.any.isRequired,
};

// The SuccessToast, ErrorMessage, Header, and Footer components are used in
// multiple different drawers with different text. We may want to dry up this
// code eventually to avoid duplicating functionality within individaul
// drawers. The same can be said for many of the styled components as well.

function SuccessToast({ success }) {
  const fetcher = useFetcher({ key: 'configureNotifications' });
  const { announceSnackbar } = useSnackbarForMessage();
  const dispatch = useDispatch();

  useEffect(() => {
    if (success) {
      announceSnackbar(
        'Your notification preferences have been updated successfully.',
        'success'
      );
      dispatch(
        setGoToConfigureNotifications({ goToConfigureNotifications: false })
      );
    }

    // Return a cleanup function that gets called when the component unmounts
    return () => {
      if (success) {
        fetcher.submit({ idle: true }, { method: 'post', action: '/' });
      }
    };
  }, [success]);

  return null;
}

SuccessToast.propTypes = {
  success: PropTypes.bool,
};

function ErrorMessage() {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1}
      sx={{ gridArea: 'error' }}
    >
      <ErrorIconWrapper />
      <ErrorMessageText>
        We couldn't save your notification preferences right now. Please try
        again, or reach out to our support team for assistance.
      </ErrorMessageText>
    </Stack>
  );
}

function Header() {
  const dispatch = useDispatch();
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            variant="body1"
            sx={{ color: theme.palette.custom.navyBlue.main }}
          >
            Configure Notifications
          </Typography>
        </Stack>
        <IconButton
          onClick={() => {
            dispatch(
              setGoToConfigureNotifications({
                goToConfigureNotifications: false,
              })
            );
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </>
  );
}

function Footer() {
  return (
    <>
      <Divider />
      <Box sx={{ height: '56px', width: '100%' }} />
    </>
  );
}

//##################################################################
// ### STYLED COMPONENTS
//##################################################################

// header divider content footer
// divider is incased inside the header component
const GridBox = styled(Box)`
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;
`;

const ErrorMessageText = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const ErrorIconWrapper = styled((props) => <ErrorIcon {...props} />)(
  ({ theme }) => ({
    color: theme.palette.error.main,
  })
);

const FormInputGrid = styled((props) => <Box {...props} />, {
  shouldForwardProp: (prop) => prop !== 'error',
})(({ theme, error }) => ({
  display: 'grid',
  gridTemplateAreas: `
    ${error ? "'error'" : ''}
    'header'
    'checkboxes'
    'save'
    `,
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
}));

const FormHeader = styled(Box)(({ theme }) => ({
  gridArea: 'header',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SubTitle = styled((props) => (
  <Typography variant="subtitle2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Blurb = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const CheckboxStack = styled(Stack)(() => ({
  gridArea: 'checkboxes',
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: `-${theme.spacing(0.5)}`,
  },
}));

const CheckboxLabel = styled((props) => (
  <Typography variant="subtitle2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
}));

//##################################################################
// ### HELPERS
//##################################################################

// we want to only mark the form as valid if something has changed relative to the initial starting state, otherwise it will not be valid and the save button will be disabled
const useIsModifiedForm = (data, ref) => {
  if (!data || !ref.current) return false;

  return Object.values(FORM_IDS).some(
    (key) => data[key]?.emailEnabled !== ref.current[key]?.checked
  );
};
